import { CONCRETE_ZONES, ConcreteZone, PALLET, Point2, SITE_BUILDINGS, SITE_CHIMNEY } from '../config/factorySite';
import { LagerLayout } from '../types';

export function pointInPolygon([x, z]: Point2, polygon: Point2[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, zi] = polygon[i];
    const [xj, zj] = polygon[j];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}

function segmentsCross(a: Point2, b: Point2, c: Point2, d: Point2): boolean {
  const cross = (o: Point2, p: Point2, q: Point2) => (p[0] - o[0]) * (q[1] - o[1]) - (p[1] - o[1]) * (q[0] - o[0]);
  const d1 = cross(c, d, a);
  const d2 = cross(c, d, b);
  const d3 = cross(a, b, c);
  const d4 = cross(a, b, d);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

export function layoutCorners(l: LagerLayout): Point2[] {
  const hw = l.width / 2;
  const hd = l.depth / 2;
  return [
    [l.x - hw, l.z - hd],
    [l.x + hw, l.z - hd],
    [l.x + hw, l.z + hd],
    [l.x - hw, l.z + hd]
  ];
}

// Betonska zona u kojoj se pravougaonik nalazi u celosti (ili null)
export function zoneContaining(l: LagerLayout, zones: ConcreteZone[] = CONCRETE_ZONES): ConcreteZone | null {
  const corners = layoutCorners(l);
  for (const zone of zones) {
    if (!corners.every((c) => pointInPolygon(c, zone.polygon))) continue;
    const poly = zone.polygon;
    let crosses = false;
    for (let i = 0; i < 4 && !crosses; i++) {
      for (let j = 0, k = poly.length - 1; j < poly.length; k = j++) {
        if (segmentsCross(corners[i], corners[(i + 1) % 4], poly[k], poly[j])) {
          crosses = true;
          break;
        }
      }
    }
    if (!crosses) return zone;
  }
  return null;
}

export function layoutsOverlap(a: LagerLayout, b: LagerLayout): boolean {
  return Math.abs(a.x - b.x) * 2 < a.width + b.width && Math.abs(a.z - b.z) * 2 < a.depth + b.depth;
}

// Zgrada ili dimnjak na koji bi lokacija bila nacrtana (ili null)
export function buildingAt(l: LagerLayout, ignoreIds: string[] = []): string | null {
  for (const b of SITE_BUILDINGS) {
    if (ignoreIds.includes(b.id)) continue;
    if (layoutsOverlap(l, { x: b.x, z: b.z, width: b.width, depth: b.depth })) return b.label || 'zgrada';
  }
  const d = SITE_CHIMNEY.radius * 2 + 1;
  if (layoutsOverlap(l, { x: SITE_CHIMNEY.x, z: SITE_CHIMNEY.z, width: d, depth: d })) return 'dimnjak';
  return null;
}

export function layoutFromPoints(a: Point2, b: Point2): LagerLayout {
  return {
    x: (a[0] + b[0]) / 2,
    z: (a[1] + b[1]) / 2,
    width: Math.abs(b[0] - a[0]),
    depth: Math.abs(b[1] - a[1])
  };
}

// Redovi idu duž duže strane lokacije
export function rowsAlongX(l: LagerLayout): boolean {
  return l.width >= l.depth;
}

// Predlog redova za nacrtanu površinu: palete poprečno (1.2 m) + mali razmak, mesta po 0.8 m duž reda
export function suggestRows(l: LagerLayout): { count: number; capacity: number } {
  const length = Math.max(l.width, l.depth);
  const across = Math.min(l.width, l.depth);
  return {
    count: Math.max(1, Math.floor(across / (PALLET.length + 0.2))),
    capacity: Math.max(1, Math.floor(length / (PALLET.width + 0.1)))
  };
}

export const snap = (v: number, step = 0.5) => Math.round(v / step) * step;

// Boja po popunjenosti: zeleno < 80%, žuto < 100%, crveno = puno
export const occupancyColor = (pct: number) => (pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e');
